import { useState, useEffect, useMemo, useCallback } from 'react'
import { Button, Input, Slider } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  editBackgroundImageAtom,
  pageThemeOverrideSelector,
  pageLayoutAtom,
  showPinturaAtom,
  themeOverrideAtom,
  unsavedChangesAtom,
} from 'atoms'
import { useApplyThemeOverridesToPages } from 'hooks'
import { default as _omit } from 'lodash/omit'
import { ToggleSwitch } from 'components/global/ToggleSwitch/ToggleSwitch'
import { useTranslation } from 'react-i18next'
import { parseBackgroundFill } from '@/utils/editor/backgroundUtils'

export const BackgroundMenu = () => {
  const { t } = useTranslation()
  const applyThemeOverridesToPages = useApplyThemeOverridesToPages()
  const setShowPintura = useSetRecoilState(showPinturaAtom)
  const setEditBackgroundImage = useSetRecoilState(editBackgroundImageAtom)
  const [pageLayout, setPageLayout] = useRecoilState(pageLayoutAtom)
  const themeOverride = useRecoilValue(themeOverrideAtom)

  const [hideBackground, setHideBackground] = useRecoilState(
    pageThemeOverrideSelector({ path: 'hide_background' }),
  )
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)
  // The toggle writes only on a click, so it marks the page unsaved here. The
  // selector does not mark: opening this tab writes a slider's default
  // through it.
  const toggleHideBackground = useCallback(
    (hide) => {
      setHideBackground(hide)
      setUnsavedChanges(true)
    },
    [setHideBackground, setUnsavedChanges],
  )

  const [backgroundY, setBackgroundY] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_top' }),
  )

  const [backgroundX, setBackgroundX] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_left' }),
  )

  const [backgroundSize, setBackgroundSize] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_fill' }),
  )

  const [backgroundOpacity, setBackgroundOpacity] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_opacity' }),
  )

  // Keep the parsed percentage values memoized so the slider controls stay in sync
  const parsedBackgroundFill = useMemo(
    () => parseBackgroundFill(backgroundSize),
    [backgroundSize],
  )

  const [backgroundWidth, setBackgroundWidth] = useState(
    parsedBackgroundFill.backgroundWidthValue,
  )

  const [backgroundHeight, setBackgroundHeight] = useState(
    parsedBackgroundFill.backgroundHeightValue,
  )

  const revertBackground = () => {
    const omit = [
      'id',
      'available',
      'paper_type',
      'layout_id',
      'product_id',
      'theme_id',
      'archived',
    ]
    const sanitizedThemeOverride = _omit(themeOverride, omit)

    const resolvedBackgroundTop =
      typeof sanitizedThemeOverride.background_top === 'number'
        ? sanitizedThemeOverride.background_top
        : 0

    const resolvedBackgroundLeft =
      typeof sanitizedThemeOverride.background_left === 'number'
        ? sanitizedThemeOverride.background_left
        : 0

    const resolvedBackgroundFill =
      typeof sanitizedThemeOverride.background_fill === 'string' &&
      sanitizedThemeOverride.background_fill.trim() !== ''
        ? sanitizedThemeOverride.background_fill
        : 'auto 105%'

    const parsedResolvedBackgroundFill = parseBackgroundFill(
      resolvedBackgroundFill,
    )

    const revertedPageLayout = {
      ...pageLayout,
      ...sanitizedThemeOverride,
      background_top: resolvedBackgroundTop,
      background_left: resolvedBackgroundLeft,
      background_fill: resolvedBackgroundFill,
      background_opacity: 0,
      background_edits: {},
    }

    setBackgroundY(resolvedBackgroundTop)
    setBackgroundX(resolvedBackgroundLeft)
    setBackgroundWidth(parsedResolvedBackgroundFill.backgroundWidthValue)
    setBackgroundHeight(parsedResolvedBackgroundFill.backgroundHeightValue)
    setBackgroundSize(resolvedBackgroundFill)
    setBackgroundOpacity(0)
    setPageLayout(revertedPageLayout)
  }

  const applyChanges = (selectedPages) => {
    applyThemeOverridesToPages(selectedPages, {
      ...pageLayout,
      background_top: backgroundY,
      background_left: backgroundX,
      background_fill: backgroundSize,
      background_opacity: backgroundOpacity / 100,
    })
  }

  useEffect(() => {
    setBackgroundWidth(parsedBackgroundFill.backgroundWidthValue)
  }, [parsedBackgroundFill.backgroundWidthValue])

  useEffect(() => {
    setBackgroundHeight(parsedBackgroundFill.backgroundHeightValue)
  }, [parsedBackgroundFill.backgroundHeightValue])

  // Compose the background fill string and only push a Recoil update when something actually changed
  const commitBackgroundSize = useCallback(
    (nextWidth, nextHeight) => {
      const resolvedWidth =
        nextWidth === 'auto' || nextWidth === '' ? 'auto' : `${nextWidth}%`
      const resolvedHeight =
        nextHeight === 'auto' || nextHeight === '' ? 'auto' : `${nextHeight}%`
      const nextBackgroundSize = `${resolvedWidth} ${resolvedHeight}`

      if (nextBackgroundSize !== backgroundSize) {
        setBackgroundSize(nextBackgroundSize)
      }
    },
    [backgroundSize, setBackgroundSize],
  )

  // Helper to update width slider state while guarding against runaway updates
  const handleBackgroundWidthChange = useCallback(
    (nextWidth) => {
      const normalizedWidth =
        nextWidth === '' || nextWidth === null || nextWidth === undefined
          ? ''
          : `${nextWidth}`

      setBackgroundWidth(normalizedWidth)

      if (normalizedWidth === '') {
        return
      }

      commitBackgroundSize(normalizedWidth, backgroundHeight)
    },
    [backgroundHeight, commitBackgroundSize],
  )

  // Helper to update height slider state while guarding against runaway updates
  const handleBackgroundHeightChange = useCallback(
    (nextHeight) => {
      const normalizedHeight =
        nextHeight === '' || nextHeight === null || nextHeight === undefined
          ? ''
          : `${nextHeight}`

      setBackgroundHeight(normalizedHeight)

      if (normalizedHeight === '') {
        return
      }

      commitBackgroundSize(backgroundWidth, normalizedHeight)
    },
    [backgroundWidth, commitBackgroundSize],
  )

  useEffect(() => {
    setEditBackgroundImage(true)

    return () => setEditBackgroundImage(false)
    // eslint-disable-next-line
  }, [])

  return (
    <Flex column padding="16px">
      <Text margin="0 0 16px">
        {t('editor.menus.properties.background.positionText')}
      </Text>
      <Flex margin="0 0 16px">
        <Input
          value={[backgroundX, setBackgroundX]}
          label={t('editor.menus.properties.background.leftText')}
          margin="0 16px 0 0"
          placeholder="100"
          type="number"
        />
        <Input
          value={[backgroundY, setBackgroundY]}
          label={t('editor.menus.properties.background.topText')}
          margin="0 16px 0 0"
          placeholder="100"
          type="number"
        />
      </Flex>
      <Text margin="0 0 16px">
        {t('editor.menus.properties.background.sizeText')}
      </Text>
      {backgroundWidth !== 'auto' && (
        <Flex full column align="flex-start" margin="0 0 20px">
          <Text size="12px" weight="500">
            {t('editor.menus.properties.background.widthFillText')}
          </Text>
          <Slider
            sliderState={[backgroundWidth, handleBackgroundWidthChange]}
            min="0"
            max="400"
            initialValue={100}
          />
        </Flex>
      )}
      {backgroundHeight !== 'auto' && (
        <Flex full column align="flex-start" margin="0 0 20px">
          <Text size="12px" weight="500">
            {t('editor.menus.properties.background.heightFillText')}
          </Text>
          <Slider
            sliderState={[backgroundHeight, handleBackgroundHeightChange]}
            min="0"
            max="400"
            initialValue={100}
          />
        </Flex>
      )}
      <Flex full column align="flex-start" margin="0 0 20px">
        <Text size="12px" weight="500" margin="0 24px 0 0">
          {t('editor.menus.properties.background.opacityText')}
        </Text>
        <Slider
          sliderState={[
            !isNaN(backgroundOpacity) ? backgroundOpacity : 0,
            setBackgroundOpacity,
          ]}
          min="0"
          max="100"
          initialValue={0}
        />
      </Flex>
      <Flex margin="0 0 16px">
        <ToggleSwitch
          label={t('editor.menus.properties.background.hideBackgroundLabel')}
          value={[hideBackground, toggleHideBackground]}
          labelLeft
          data-testid="background-hide-toggle"
          data-hide-background={hideBackground ? 'true' : 'false'}
        />
      </Flex>
      <Button center margin="0 0 16px" onClick={() => setShowPintura(true)}>
        {t('editor.menus.properties.background.editImageButton')}
      </Button>
      <Button
        center
        margin="0 0 16px"
        onClick={() => revertBackground()}
        data-testid="background-revert"
      >
        {t('editor.menus.properties.background.revertImageButton')}
      </Button>
      <Button
        center
        margin="0 0 16px"
        onClick={() => applyChanges('even')}
        data-testid="background-apply-even-pages"
      >
        {t('editor.menus.properties.background.applyEvenPagesButton')}
      </Button>
      <Button
        center
        margin="0 0 16px"
        onClick={() => applyChanges('odd')}
        data-testid="background-apply-odd-pages"
      >
        {t('editor.menus.properties.background.applyOddPagesButton')}
      </Button>
      <Button
        center
        margin="0 0 16px"
        onClick={() => applyChanges('all')}
        data-testid="background-apply-all-pages"
      >
        {t('editor.menus.properties.background.applyAllPagesButton')}
      </Button>
    </Flex>
  )
}

export default BackgroundMenu
